/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
import { LogLevel } from '@azure/msal-browser';

export const API_URL = process.env.REACT_APP_API_URL || '';
const scopes = process.env.REACT_APP_SCOPES || '';
const clientId = process.env.REACT_APP_CLIENT_ID || '';
const redirectUri = process.env.REACT_APP_REDIRECT_URL || '';
const postLogoutRedirectUri = process.env.REACT_APP_POST_LOGOUT_REDIRECT_URL || '';
const authorityBaseUrl = process.env.REACT_APP_AUTHORITY_BASE_URL || '';
const authorityDomain = process.env.REACT_APP_AUTHORITY_DOMAIN || '';

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    signUpSignIn: 'B2C_1A_SIGNUP_SIGNIN_V2',
    forgotPassword: 'B2C_1A_PASSWORDRESET_V2',
    changePassword: 'B2C_1A_CHANGEPASSWORD_V2',
  },
  authorityBaseUrl,
  authorityDomain,
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId, // This is the ONLY mandatory field that you need to supply.
    authority: b2cPolicies.authorityBaseUrl + b2cPolicies.names.signUpSignIn, // Use a sign-up/sign-in user-flow as a default authority
    changePassword: b2cPolicies.authorityBaseUrl + b2cPolicies.names.changePassword, // Use a sign-up/sign-in user-flow as a default authority
    forgotPassword: b2cPolicies.authorityBaseUrl + b2cPolicies.names.forgotPassword, // Use a sign-up/sign-in user-flow as a default authority
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri, // Points to window.location.origin. You must register this URI on Azure Portal/App Registration.
    postLogoutRedirectUri, // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: true, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: 'localStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      // Set log level to Error only - suppresses Info, Verbose, and Warning logs
      // This keeps the production console clean while still logging errors
      logLevel: LogLevel.Error,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        // Only log errors to keep console clean in hosted environments
        if (level === LogLevel.Error) {
          console.error(message);
        }
      },
    },
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const protectedResources = {
  api: {
    dotnetApiEndpoint: API_URL,
    apiEndpoint: API_URL,
    scopes: [scopes],
  },
};

export const loginRequest = {
  scopes: [...protectedResources.api.scopes],
};
/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
export const silentRequest = {
  scopes: ['openid', 'profile'],
  loginHint: 'example@domain.net',
};
